import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Settings,
  MonitorUp,
  MessageSquare,
  PhoneOff,
} from 'lucide-react';
import { cn } from '../lib/utils';

export type CallControlIcon =
  | 'mic'
  | 'mic-off'
  | 'video'
  | 'video-off'
  | 'settings'
  | 'screen-share'
  | 'message-square'
  | 'phone-off';

export interface CallControlButtonProps {
  icon: CallControlIcon;
  label?: string;
  isActive?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'danger';
  onClick?: () => void;
  className?: string;
  'aria-label'?: string;
}

const iconMap = {
  mic: Mic,
  'mic-off': MicOff,
  video: Video,
  'video-off': VideoOff,
  settings: Settings,
  'screen-share': MonitorUp,
  'message-square': MessageSquare,
  'phone-off': PhoneOff,
} as const;

export const CallControlButton = ({
  icon,
  label,
  isActive = false,
  disabled = false,
  variant = 'default',
  onClick,
  className,
  'aria-label': ariaLabel,
}: CallControlButtonProps) => {
  const Icon = iconMap[icon];

  const isDanger = variant === 'danger';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || label}
      className={cn(
        'flex flex-col items-center justify-center gap-1 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <div
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-xl transition-colors',
          isDanger
            ? 'w-16 rounded-full bg-red-500 text-white hover:bg-red-600'
            : isActive
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      {label && (
        <span
          className={cn(
            'text-xs font-normal',
            isDanger ? 'text-red-500' : 'text-slate-500'
          )}
        >
          {label}
        </span>
      )}
    </button>
  );
};
